import { ErrorEvent } from '@sentry/types'

type IgnoreType = 'TypeError' | 'SyntaxError' | 'AxiosError'
type SkipThirdPartyMode = 'single' | 'all'

interface SentryOptions {
  ignoreWords?: string[],
  ignoreTypes?: IgnoreType[],
  ignoreErrors?: RegExp[],
  skipThirdParty?: boolean,
  skipThirdPartyMode?: SkipThirdPartyMode,
  perHourLimit?: number,
}

const logPrefix = '[SENTRY]'

const log = {
  info: (message: string, ...args: any[]) => {
    console.log(`%c${logPrefix} %cINFO%c ${message}`, 'color: purple;', 'color: gray;', 'color: inherit;', ...args)
  },
  warn: (message: string, ...args: any[]) => {
    console.warn(`%c${logPrefix} %cWARN%c ${message}`, 'color: orange;', 'color: gray;', 'color: inherit;', ...args)
  },
  error: (message: string, ...args: any[]) => {
    console.error(`%c${logPrefix} %cERROR%c ${message}`, 'color: red;', 'color: gray;', 'color: inherit;', ...args)
  },
}

const errorCountMap: Record<string, number> = {}

const shouldIgnoreWord = (errorTitle: string, ignoreWords: string[]): boolean => {
  const errorDetails = errorTitle.split(' ').map(detail => detail.replace(/[^a-zA-Z0-9]/g, ''))
  return errorDetails.some(detail => ignoreWords.includes(detail))
}

const shouldIgnoreType = (errorType: string, ignoreTypes: IgnoreType[]): boolean => {
  return ignoreTypes.includes(errorType as IgnoreType)
}

const shouldSkipThirdParty = (errorFrames: any[], skipThirdPartyMode: SkipThirdPartyMode): boolean => {
  if (skipThirdPartyMode === 'single') {
    return errorFrames.some(frame => frame.in_app === false)
  } else if (skipThirdPartyMode === 'all') {
    return errorFrames.every(frame => frame.in_app === false)
  }
  return false
}
export const useSentry = (
  {
    ignoreWords = [],
    ignoreTypes = [],
    ignoreErrors = [],
    skipThirdParty = false,
    skipThirdPartyMode = 'single',
    perHourLimit = 100
  }: SentryOptions
) => {
  const beforeSend = (event: ErrorEvent) => {
    const exception = event.exception?.values?.[0]
    const errorType = exception?.type
    const errorTitle = exception?.value
    const errorFrames = exception?.stacktrace?.frames
    const fingerprint = errorTitle || 'unknown'

    log.info('New error event received:', { errorType, errorTitle })

    if (errorTitle && perHourLimit) {
      errorCountMap[fingerprint] = (errorCountMap[fingerprint] || 0) + 1
      if (errorCountMap[fingerprint] > perHourLimit) {
        log.warn(`Error "${fingerprint}" exceeded rate limit (${perHourLimit}/hour). Event dropped.`)
        return null
      }
    }

    if (errorTitle && ignoreWords.length) {
      const isIgnored = shouldIgnoreWord(errorTitle, ignoreWords)
      if (isIgnored) {
        log.warn(`Error "${fingerprint}" matched ignoreWords. Event dropped.`)
        return null
      }
    }

    if (errorType && ignoreTypes.length) {
      const isIgnored = shouldIgnoreType(errorType, ignoreTypes)
      if (isIgnored) {
        log.warn(`Error type "${errorType}" matched ignoreTypes. Event dropped.`)
        return null
      }
    }

    if (errorFrames?.length && skipThirdParty && skipThirdPartyMode) {
      const isIgnored = shouldSkipThirdParty(errorFrames, skipThirdPartyMode)
      if (isIgnored) {
        log.warn(`Error "${fingerprint}" came from 3rd-party code and skipThirdParty is enabled. Event dropped.`)
        return null
      }
    }

    log.error('Sending error to Sentry:', event)
    return event
  }

  return {
    ignoreErrors,
    beforeSend
  }
}
